/**
 * The Eblast (email) report as a template document — the legacy issued PDF layout, carrying the V2 icon
 * set and card styling (`iconStyle: 'v2'`) so it matches the other V2 channel templates.
 *
 * Structure (page KINDS; the drop page repeats, so a two-drop SO renders four pages):
 *   1. Eblast Summary  (fixed) — creative + client meta, the two headline KPIs, the device-category and
 *      channel cards, and the mobile-device-details + browser-type breakdowns.
 *   2. Campaign Details / Eblast Overview (flowing) — the per-drop table, the clicks-by-day line chart,
 *      and the interaction%/click% grouped bar.
 *   3+. Campaign Details / Eblast Link Detail (flowing, one per drop) — the deployment creative beside its
 *      link performance table (Link / Clicks / CTR).
 *
 * The band title differs by page ("Eblast Summary" vs "Campaign Details"), so each page carries its own
 * chrome `header` override (engine-placed like document chrome — C4 — never authored into the flow).
 *
 * No per-drop component, no email-specific React: the renderer cannot tell this document from the display
 * one. Device / browser breakdowns come from `overall.devices` / `overall.browsers`, populated by the
 * worker from the aggregate's reduced click maps.
 */

import type { BlockInstanceT, Template } from './template-schema.js';

/**
 * The dark band, repeated per page. Right side: the deployment name + CAMPAIGN ID#; left: the report
 * title, "Campaign Evaluation Report" on every page to match the other V2 formats. The per-page metadata
 * `title` still names each page for the builder, exactly as the display template does.
 */
const pageHeader = (): BlockInstanceT[] => [
  {
    id: 'hdr',
    type: 'PageHeader',
    props: { title: 'Campaign Evaluation Report', showAccentRule: true, campaignIdLabel: 'CAMPAIGN ID#:' },
    bindings: { clientName: 'campaign.name', campaignId: 'campaign.reportSo', logo: 'presentation.logo', showLogo: 'presentation.showLogo' },
  },
];

export const EMAIL_TEMPLATE: Template = {
  schemaVersion: 1,
  id: 'white-label-default-email',
  name: 'V2 — Eblast',
  version: 1,
  status: 'draft',
  agencyId: null,
  channels: ['EMAIL'],
  lifecycle: ['PREFLIGHT', 'INTERIM', 'POSTFLIGHT'],

  theme: {
    // V2 icon set + card styling: the shared summary cards (Agency/Start/End rows, KPI tiles, Device
    // Category / Details, Channels, Browser) render the approved raster marks and the V2 card treatment,
    // matching the other V2 channel templates. Drives IconStyleContext in the renderer (Document.tsx).
    iconStyle: 'v2',
    // Share the V2 colour system the other V2 templates use (display / audio / CTV / DOOH), so the Eblast
    // reads as one of them and not its own steel-blue variant. This gives the deep-navy header band and
    // card heads, the bright cyan accent (the "CAMPAIGN ID#" value and the header rule — the mid-blue
    // accent on a steel band left the id almost unreadable), and the light-grey page the cards float on.
    color: {
      primary: '#001345',
      accent: '#06CDF2',
      surface: '#FFFFFF',
      surfaceMuted: '#F5F5F5',
      border: '#E8E8E8',
      textPrimary: '#0A0A0A',
      textInverse: '#FFFFFF',
      headerFrom: '#000A1E',
      headerTo: '#00174B',
      page: '#F1F3F6',
      stripBorder: '#2B5FA8',
    },
    typography: { fontFamily: 'Roboto', baseSizePt: 11, scale: 1.21 },
    page: { widthIn: 8.5, heightIn: 11, marginIn: 0 },
    branding: { companyName: 'ProData Media', copyrightName: 'ProData Media' },
    // The V2 card layout knobs the other templates carry: the waves page art, and the equal-height floor
    // and spacing for the summary breakdown cards so the rows sit like the display report's.
    layout: { breakdownMinHeightIn: 1.55, pageBackground: 'waves', summaryRowSpaceIn: 0.06, detailsTopSpaceIn: 0.38 },
  },

  chrome: {
    // Default header; every page overrides the title via its own `header`, but the document still needs one.
    header: pageHeader(),
    footer: [
      {
        id: 'ftr',
        type: 'PageFooter',
        props: {},
        // The copyright names the AGENCY (D: reports carry the agency's copyright, not the client's).
        bindings: { companyName: 'campaign.agencyName', year: '$substring(report.periodEnd, 0, 4)' },
      },
    ],
  },

  pages: [
    // ------------------------------------------------------------- page 1 --
    {
      id: 'summary',
      mode: 'fixed',
      title: 'Eblast Summary',
      header: pageHeader(),
      blocks: [
        // Creative (left) beside the client / flight meta (right).
        {
          id: 'summary-top',
          type: 'Columns',
          // Equal 50/50 columns that stretch to the same height — matching the display report's
          // identity-row and the two card rows below, so the creative and meta boxes line up with them.
          props: { columns: 2 },
          bindings: {},
          children: [
            {
              id: 'creative',
              // The email deployment capture is a tall portrait; `fillTop` fills the box width and crops
              // to the top so it reads large (the mockup shows the top of the creative), and the taller
              // cap lets it stand as tall as the identity card beside it in the equal-height row.
              type: 'CreativePreview',
              props: { maxHeightIn: 3.1, fillTop: true, footnote: 'Representative of creative used in campaign.' },
              bindings: { assetId: 'email.drops[0].creativeUrl ? email.drops[0].creativeUrl : campaign.bannerAssetId' },
            },
            {
              id: 'meta',
              type: 'CampaignMetaCard',
              props: { variant: 'labels', showNote: true },
              bindings: {
                clientName: 'campaign.clientName',
                startDate: 'campaign.startDate',
                endDate: 'campaign.endDate',
                campaignId: 'campaign.reportSo',
                note: 'campaign.notes',
              },
            },
          ],
        },
        // The headline figures as the V2 contract-strip band (the display report's row band), not big
        // tiles. Four campaign-wide cells:
        //   - Total Records / Total Clicks: the summed-across-drops totals (email.recipients / email.clicks).
        //   - Avg. Int. Rate / Avg. CTR%: the SIMPLE AVERAGE of each per-drop rate ($average over the drops),
        //     as requested — note this is the unweighted mean, not the campaign-wide weighted rate
        //     (email.openRate / email.ctr), which is total opens|clicks ÷ total records.
        {
          id: 'kpis',
          type: 'DeliveryStrip',
          props: { ctrFootnote: '' },
          bindings: {
            metrics: `[
              {"label": "Total Records",  "value": email.recipients,              "format": "int"},
              {"label": "Total Clicks",   "value": email.clicks,                  "format": "int"},
              {"label": "Avg. Int. Rate", "value": $average(email.drops.openRate), "format": "pct"},
              {"label": "Avg. CTR%",      "value": $average(email.drops.ctr),      "format": "pct"}
            ]`,
          },
        },
        // Device category (mobile vs desktop clicks) beside the campaign channel.
        {
          id: 'category-row',
          type: 'Columns',
          props: { columns: 2 },
          bindings: {},
          children: [
            {
              id: 'device-split',
              type: 'DeviceCategoryCard',
              props: { artHeightPx: 30 },
              bindings: { mobile: 'email.mobileClicks', desktop: 'email.nonMobileClicks' },
            },
            {
              id: 'channels',
              type: 'CampaignChannelsCard',
              props: {},
              bindings: { channels: '["EBLAST"]' },
            },
          ],
        },
        // The mobile-device breakdown (sums to mobile clicks) beside the browser breakdown (sums to total).
        {
          id: 'breakdown-row',
          type: 'Columns',
          props: { columns: 2 },
          bindings: {},
          children: [
            {
              id: 'device-details',
              type: 'DeviceDetailsTable',
              props: { maxRows: 8, title: 'Mobile Device Details', showNote: false },
              bindings: { rows: 'overall.devices' },
            },
            {
              id: 'browsers',
              type: 'BrowserTypeCard',
              props: { maxItems: 5, imageLogos: true },
              bindings: { rows: 'overall.browsers' },
            },
          ],
        },
      ],
    },

    // ------------------------------------------------ geo (optional) --
    // Optional geo targeting page: shown only when the reviewer turns on Geographic Targeting (off by
    // default, like the other channels' targeting). An eblast has no ad geo-targeting, so the map plots
    // the cities recipients CLICKED from — targeting.cities, derived from citiesByClicks in the worker.
    // Post-flight only: there are no clicked cities before the campaign sends.
    {
      id: 'geo',
      mode: 'fixed',
      title: 'Geographic Targeting',
      header: pageHeader(),
      lifecycle: ['INTERIM', 'POSTFLIGHT'],
      visibleWhen: 'presentation.showGeoTargeting = true',
      blocks: [
        {
          id: 'geo-card',
          type: 'SectionCard',
          props: { title: 'Geographic Targeting', icon: 'banner-geo-targeting' },
          bindings: {},
          children: [
            {
              id: 'geo-map',
              type: 'GeoTargetingMap',
              // Email has no ad geo-targeting — this maps click geography, so the footnote says so rather
              // than the default "City targeting shown as approximate ZIP-level coverage".
              props: { heightIn: 5.5, cityFootnote: 'Cities where recipients clicked.' },
              bindings: {
                mapImage: 'targeting.mapImage',
                // Offline city map: plots the clicked cities. geoMode defaults to 'city' when cities exist.
                geoMode: "targeting.geoMode ? targeting.geoMode : ($count(targeting.cities) > 0 ? 'city' : '')",
                cities: 'targeting.cities',
                states: 'targeting.states',
                zips: 'targeting.zips',
              },
            },
          ],
        },
      ],
    },

    // ------------------------------------------------------------- page 2 --
    {
      id: 'overview',
      // FLOWING: the drops table grows with the number of deployments (up to 14 in production).
      mode: 'flowing',
      title: 'Campaign Details',
      header: pageHeader(),
      lifecycle: ['INTERIM', 'POSTFLIGHT'],
      blocks: [
        {
          id: 'overview-card',
          type: 'SectionCard',
          props: { title: 'Eblast Overview' },
          bindings: {},
          children: [
            {
              id: 'drops-table',
              type: 'PaginatedTable',
              props: {
                /*
                 * Records (recipients), Interactions (opens) and Clicks are EDITABLE inputs — bound to
                 * raw paths and formatted here so a reviewer can correct them and the cascade rolls the
                 * change up. Int. Rate (openRate) and Click Rate (ctr) are derived: `editable: false`,
                 * so they show as computed figures and never as something to type over.
                 */
                columns: JSON.stringify([
                  { key: 'label', label: 'Drop #', widthPct: 10, format: 'text', editable: false },
                  { key: 'deployDate', label: 'Deploy Date', widthPct: 16, format: 'text', editable: false },
                  { key: 'recipients', label: 'Records', widthPct: 14, format: 'int', editable: true },
                  { key: 'opens', label: 'Interactions', widthPct: 15, format: 'int', editable: true },
                  { key: 'openRate', label: 'Int. Rate', widthPct: 13, format: 'pct', editable: false },
                  { key: 'clicks', label: 'Clicks', widthPct: 14, format: 'int', editable: true },
                  { key: 'ctr', label: 'Click Rate', widthPct: 18, format: 'pct', editable: false },
                ]),
                zebra: true,
                maxRows: 50,
              },
              bindings: {
                rows: 'email.drops',
                totalRows: '$count(email.drops)',
              },
            },
          ],
        },
        {
          id: 'by-day-card',
          type: 'SectionCard',
          props: { title: 'Eblast Clicks by Day' },
          bindings: {},
          children: [
            {
              id: 'clicks-by-day',
              type: 'SeriesChart',
              props: { chartType: 'line', heightIn: 2.3, xLabel: 'Date', yLabel: '#Clicks', valueFormat: 'int', showLegend: false },
              bindings: {
                categories: 'email.clicksByDay.date',
                series: '[ {"name": "#Clicks", "values": email.clicksByDay.clicks} ]',
              },
            },
          ],
        },
        {
          id: 'opens-clicks-card',
          type: 'SectionCard',
          props: { title: 'Eblast Click & Interactions by Deployment' },
          bindings: {},
          children: [
            {
              id: 'opens-vs-clicks',
              type: 'SeriesChart',
              props: { chartType: 'bar', heightIn: 2.4, xLabel: 'Drops', yLabel: 'Interaction% vs Click%', valueFormat: 'pct', showLegend: true },
              bindings: {
                categories: 'email.drops.label',
                series: `[
                  {"name": "Interaction%", "values": email.drops.openRate},
                  {"name": "Click%",       "values": email.drops.ctr}
                ]`,
              },
            },
          ],
        },
      ],
    },

    // ------------------------------------------------------------- page 3+ --
    // One page per deployment: its creative beside its link-performance table.
    {
      id: 'drop-detail',
      mode: 'flowing',
      title: 'Campaign Details',
      header: pageHeader(),
      lifecycle: ['INTERIM', 'POSTFLIGHT'],
      repeat: { over: 'email.drops', as: 'drop', emptyBehaviour: 'hide' },
      blocks: [
        {
          id: 'drop-heading',
          type: 'RichTextBlock',
          props: {},
          bindings: {
            html: `"<b style='color:#001345;font-size:12pt'>EBLAST LINK DETAIL – Drop #: " & $string($drop.dropNo)
                   & ($drop.title ? " (" & $drop.title & ")" : "") & "</b>"`,
          },
        },
        {
          id: 'drop-split',
          type: 'Columns',
          props: { columns: 2, align: 'start', weights: '3,5' },
          bindings: {},
          children: [
            {
              // The full deployment creative (tall portrait capture), height-capped so the row clears the
              // footer. The cap must leave room for a TALL header + the two-line drop heading: the creative
              // shares a breakable block with the link table, so if the header shrinks the page's content
              // height below the creative, the paginator splits the table rows but cannot shrink the fixed
              // creative — it then overflows into the copyright/page-number footer. 8.4in left only ~46px of
              // clearance and a slightly taller header tipped the last link rows over the footer; 7.4in keeps
              // ~140px of clearance across realistic header/heading heights while the creative stays large.
              id: 'drop-creative',
              type: 'CreativePreview',
              props: { maxHeightIn: 7.4, footnote: 'Representative of creative used in campaign.' },
              // When the click heatmap is on in OVERLAY mode, the creative slot shows the heatmap image
              // (`heatmap:<io>`, generated at render); otherwise the plain deployment screenshot.
              bindings: { assetId: '(presentation.showClickHeatmap and $not(presentation.heatmapDedicatedPage) and $drop.io) ? ("heatmap:" & $drop.io) : ($drop.creativeUrl ? $drop.creativeUrl : campaign.bannerAssetId)' },
            },
            {
              id: 'drop-links-card',
              type: 'SectionCard',
              props: { title: 'Link Performance' },
              bindings: {},
              children: [
                {
                  id: 'drop-links',
                  type: 'PaginatedTable',
                  props: {
                    // Per-link Clicks is EDITABLE (raw path, formatted here); editing it rolls up to the
                    // drop's clicks → the campaign total → the rates and charts. CTR (the link's share of
                    // the drop's clicks) is derived — `editable: false`, recomputed by the cascade.
                    columns: JSON.stringify([
                      { key: 'link', label: 'Link', widthPct: 76, breakWords: true, format: 'text', editable: false },
                      { key: 'clicks', label: 'Clicks', widthPct: 12, format: 'int', editable: true },
                      { key: 'clickShare', label: 'CTR', widthPct: 12, format: 'pct', editable: false },
                    ]),
                    zebra: true,
                    fixedLayout: true,
                    // Legacy calls the share column "CTR" — it is the link's share of the drop's clicks.
                    // `breakWords` on the link column wraps long destination URLs so the figures stay put.
                    maxRows: 40,
                  },
                  bindings: {
                    rows: '$drop.links',
                    totalRows: '$count($drop.links)',
                  },
                },
              ],
            },
          ],
        },
      ],
    },

    // ------------------------------------------------------- heatmap page --
    // A dedicated click-heatmap page per drop — ONLY when the heatmap is on and set to its own page.
    // In overlay mode this page is hidden (the heatmap rides on the Link Detail creative instead).
    {
      id: 'drop-heatmap',
      mode: 'flowing',
      title: 'Campaign Details',
      header: pageHeader(),
      lifecycle: ['INTERIM', 'POSTFLIGHT'],
      visibleWhen: 'presentation.showClickHeatmap and presentation.heatmapDedicatedPage',
      repeat: { over: 'email.drops', as: 'drop', emptyBehaviour: 'hide' },
      blocks: [
        {
          id: 'heatmap-heading',
          type: 'RichTextBlock',
          props: {},
          bindings: {
            html: `"<b style='color:#001345;font-size:12pt'>EBLAST CLICK HEATMAP – Drop #: " & $string($drop.dropNo)
                   & ($drop.title ? " (" & $drop.title & ")" : "") & "</b>"`,
          },
        },
        {
          id: 'heatmap-image',
          type: 'CreativePreview',
          props: { maxHeightIn: 8.6, footnote: 'Warm areas show where recipients clicked, sized by clicks.' },
          bindings: { assetId: '$drop.io ? ("heatmap:" & $drop.io) : ($drop.creativeUrl ? $drop.creativeUrl : campaign.bannerAssetId)' },
        },
      ],
    },
  ],

  createdAt: '2026-08-16T00:00:00.000Z',
  createdBy: 'm2-email',
};
